import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./AdminTools.css";

const AdminGameProjects = () => {
    const navigate = useNavigate();
    const {labId} = useParams();

    const [user, setUser] = useState(null);
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!labId) {
            setError("Lab ID is required.");
            setLoading(false);
            return;
        }

        loadProjects();
    }, [labId]);


    const loadProjects = async () => {
        try {
            setLoading(true);
            setError("");
            setSelectedProject("");
            setProjects([]);

            const response = await fetch(
                `/api/admin/game-projects?lab_id=${encodeURIComponent(labId)}`,
                {
                    method: "GET",
                    credentials: "include",
                    headers: {
                        Accept: "application/json",
                    },
                }
            );


            // Not logged in
            if (response.status === 401) {
                navigate("/rect/login");
                return;
            }


            // Not admin
            if (response.status === 403) {
                setError(
                    "You do not have permission to access this page."
                );
                return;
            }


            // Other errors
            if (!response.ok) {
                let message = "Unable to load game projects.";

                try {
                    const errorData = await response.json();

                    if (errorData?.error) {
                        message = errorData.error;
                    }
                } catch {
                    // Ignore JSON parsing error
                }

                throw new Error(message);
            }


            const data = await response.json();


            setUser(data.user || null);


            setProjects(
                Array.isArray(data.projects)
                    ? data.projects
                    : []
            );


        } catch (error) {
            console.error(
                "Game projects error:",
                error
            );

            setError(
                error.message ||
                "Unable to load game projects."
            );

            setProjects([]);

        } finally {
            setLoading(false);
        }
    };


    const selected = projects.find(
        (project) =>
            String(project.id) ===
            String(selectedProject)
    );


    const handleEditBuild = () => {

        if (!selectedProject) {
            alert("Please select a game project.");
            return;
        }


        /*
         * Pass both labId and projectId to the next page.
         *
         * Example:
         * /rect/labs/loader/krpc-5?lab_id=1
         */

        navigate(
            `/rect/labs/loader/${selectedProject}?lab_id=${encodeURIComponent(labId)}`
        );
    };


    if (loading) {
        return (
            <div className="admin-page-loading">
                <div className="spinner-border text-primary" />
                <h5>
                    Loading Game Projects...
                </h5>
            </div>
        );
    }


    return (
        <div className="admin-page">

            {/* HEADER */}

            <header className="admin-page-header">

                <button
                    className="admin-back-button"
                    onClick={() =>
                        navigate("/rect/dash")
                    }
                >
                    ← Dashboard
                </button>


                <div>

                    <div className="admin-eyebrow">
                        ADMINISTRATION
                    </div>


                    <h1>
                        Game Projects
                    </h1>


                    <p>
                        Select a game project to edit
                        and build.
                    </p>

                </div>

            </header>


            {/* ERROR */}

            {error && (
                <div className="admin-error">
                    {error}
                </div>
            )}


            {/* CONTENT */}

            <div className="admin-content">

                <div className="admin-panel">

                    <div className="admin-panel-icon">
                        🎮
                    </div>


                    <h2>
                        Select Game Project
                    </h2>


                    <p>
                        Choose a project from the list
                        below.
                    </p>


                    {/* LAB */}

                    <label>
                        Lab
                    </label>


                    <div className="admin-lab-info">
                        Lab ID: {labId}
                    </div>


                    {/* GAME PROJECT */}

                    <label>
                        Game Project
                    </label>


                    <select
                        value={selectedProject}
                        onChange={(event) =>
                            setSelectedProject(
                                event.target.value
                            )
                        }
                        className="admin-select"
                        disabled={
                            projects.length === 0
                        }
                    >

                        <option value="">
                            {projects.length === 0
                                ? "No games available"
                                : "Select a game..."
                            }
                        </option>


                        {projects.map((project) => (

                            <option
                                key={project.id}
                                value={project.id}
                            >
                                {project.name}
                            </option>

                        ))}

                    </select>


                    {/* SELECTED PROJECT */}

                    {selected && (

                        <div className="selected-project">

                            <h3>
                                {selected.name}
                            </h3>


                            <p>
                                {selected.description}
                            </p>


                            <div className="project-properties">

                                <div>
                                    <span>
                                        Language
                                    </span>

                                    <strong>
                                        {selected.language}
                                    </strong>
                                </div>


                                <div>
                                    <span>
                                        Build System
                                    </span>

                                    <strong>
                                        {selected.build_system}
                                    </strong>
                                </div>


                                <div>
                                    <span>
                                        Platform
                                    </span>

                                    <strong>
                                        {selected.platform}
                                    </strong>
                                </div>

                            </div>

                        </div>

                    )}


                    {/* EDIT / BUILD */}

                    <button
                        className="admin-primary-button"
                        onClick={handleEditBuild}
                        disabled={!selectedProject}
                    >
                        Edit & Build Project
                        <span>→</span>
                    </button>

                </div>


                {/* SUPPORTED LANGUAGES */}

                <div className="supported-projects">

                    <h3>
                        Supported Game Platforms
                    </h3>


                    <div className="supported-grid">

                        <div className="supported-card">
                            <strong>
                                ☕ Java
                            </strong>

                            <span>
                                Gradle / Maven
                            </span>
                        </div>


                        <div className="supported-card">
                            <strong>
                                📱 Android
                            </strong>

                            <span>
                                Gradle / Android SDK
                            </span>
                        </div>


                        <div className="supported-card">
                            <strong>
                                ⚙️ C++
                            </strong>

                            <span>
                                CMake / Make
                            </span>
                        </div>


                        <div className="supported-card">
                            <strong>
                                🐍 Python
                            </strong>

                            <span>
                                pip / venv
                            </span>
                        </div>

                    </div>

                </div>

            </div>

        </div>
    );
};


export default AdminGameProjects;